import React, { useEffect } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { GraduationCapIcon, TrendingUpIcon, UsersIcon, MonitorIcon } from 'lucide-react';
import { DEFAULT_HIGHLIGHTS, EDUCATION_CITATIONS, getDefaultExploreBridge, getExploreBridge } from '../data/exploreBridges';
import { resolveProgramCodeSlug } from '../data/programBridges';
import { useMetaTags } from '../hooks/useMetaTags';
import { buildClicksSearchUrl } from '../utils/clicksSearchUrl';
import { BrandedExploreBridge } from '../components/BrandedExploreBridge';
import { getUniversityBySlug } from '../data/universityData';

// Ad-copy bridge template served at /explore/{brand}-{version}
// Icons paired positionally with the outcome stats: earnings, graduates,
// online enrollment.
const HIGHLIGHT_ICONS = [TrendingUpIcon, UsersIcon, MonitorIcon];
// Brand-neutral ad copy used when an /explore/ path matches no template.
const EXPLORE_FALLBACK_SLUG = 'search-v1';
// (e.g. /explore/UAGC-v1). Layout is fixed across every template:
// site nav → hero call to action → short paragraph section → footer.
// The copy, imagery, and outbound destination come from data/exploreBridges.
export function ExploreBridgePage() {
  const {
    bridgeSlug
  } = useParams<{
    bridgeSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Fall back to the path segment so statically-registered routes (which
  // have no :bridgeSlug param) resolve the same as dynamic ones.
  const slug = bridgeSlug || location.pathname.replace(/\/+$/, '').split('/').filter(Boolean).pop();
  // Previewing the dynamic route directly (path shown as "/explore/:bridgeSlug")
  // has no real slug, so show the first template rather than a dead end.
  const isPlaceholderSlug = !slug || slug.startsWith(':');
  const bridge = isPlaceholderSlug ? getDefaultExploreBridge() : getExploreBridge(slug);
  // /explore/{degreeInterest}-{mProgram} (e.g. /explore/3-101) is shorthand
  // for the named page — redirect so the readable URL is what's shared and
  // reported on. Any query string carries over.
  const codeSlug = isPlaceholderSlug ? undefined : resolveProgramCodeSlug(slug);
  // An /explore/ path that matches nothing falls back to the brand-neutral
  // search page rather than dead-ending the visitor.
  const needsFallback = !bridge && !codeSlug;
  useEffect(() => {
    if (codeSlug) {
      navigate(`/explore/${codeSlug}${location.search}`, {
        replace: true
      });
      return;
    }
    if (needsFallback) {
      navigate(`/explore/${EXPLORE_FALLBACK_SLUG}${location.search}`, {
        replace: true
      });
    }
  }, [codeSlug, needsFallback, navigate, location.search]);
  useMetaTags({
    title: bridge?.metaTitle || 'Explore Online Degrees | FindOnlineDegrees',
    description: bridge?.metaDescription || 'Explore accredited online degree programs from our partner schools.',
    ogImage: bridge?.heroImage,
    // Ad-copy pages shouldn't compete with organic pages in search.
    robots: 'noindex, follow'
  });
  const handleNavigate = (page: string) => {
    navigate(page === 'compare' ? '/' : `/${page}`);
    window.scrollTo(0, 0);
  };
  // Carry any lead data and tracking params on this page's URL through to
  // the destination so the click can be attributed and the user isn't
  // asked for information they already provided.
  const handleCta = () => {
    if (!bridge) return;
    if (bridge.clicksSearch) {
      const url = buildClicksSearchUrl(location.search, bridge.clicksSearch);
      console.log('[Explore Bridge] redirecting to:', url);
      window.location.href = url;
      return;
    }
    if (!bridge.destinationUrl) return;
    const search = location.search.replace(/^\?/, '');
    const isExternal = /^https?:\/\//i.test(bridge.destinationUrl);
    const separator = bridge.destinationUrl.includes('?') ? '&' : '?';
    const destination = search ? `${bridge.destinationUrl}${separator}${search}` : bridge.destinationUrl;
    if (isExternal) {
      window.location.href = destination;
      return;
    }
    navigate(destination);
    window.scrollTo(0, 0);
  };
  // Branded ad copy renders the advertiser-styled layout instead.
  const university = bridge?.universitySlug ? getUniversityBySlug(bridge.universitySlug) : undefined;
  if (bridge && bridge.layout === 'branded' && university) {
    return <BrandedExploreBridge bridge={bridge} university={university} onCta={handleCta} data-id="element-1713" data-mp="gxj0g5d0q7uo" />;
  }
  // Numeric shorthand and unmatched slugs both redirect in the effect above;
  // render nothing for the frame in between rather than flashing a dead end.
  if (!bridge) return null;
  const highlights = bridge.highlights ?? DEFAULT_HIGHLIGHTS;
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-1714" data-mp="2mt3w0x5085rl">
      <Header currentPage="explore" onNavigate={handleNavigate} data-id="element-1715" data-mp="2rqn93tpyuchy" />
      <main className="flex-grow" data-id="element-1716" data-mp="19rpl8enz5r44">
        {/* Hero + call to action */}
        <section className={`relative border-b-4 border-[#e8594f] ${bridge.heroSideFade ? 'bg-[#111827]' : ''}`} data-id="element-1717" data-mp="19u1yieisfdau">
          <img src={bridge.heroImage} alt={bridge.heroImageAlt}
        // Anchored near the top of the frame: these are photographs of
        // people, and a centered crop on a wide short hero cuts their
        // faces off. On a side-fade hero the subject is pulled left so
        // the fade lands on empty background rather than across a face.
        className={`absolute inset-0 w-full h-full object-cover ${bridge.heroSideFade ? 'object-[20%_20%] lg:w-3/4' : 'object-[50%_20%]'}`} data-id="element-1718" data-mp="1it6yzqdwtgpn" />
          {bridge.heroSideFade ?
        // Image on the left, solid panel on the right, fading right to
        // left across the middle of the hero.
        <div className="absolute inset-0 bg-gray-900/60 lg:bg-[linear-gradient(to_left,#111827_0%,#111827_42%,rgba(17,24,39,0.85)_62%,rgba(17,24,39,0.45)_85%,rgba(17,24,39,0.35)_100%)]" aria-hidden="true" data-id="element-1719" data-mp="3b3vilkglu0q" /> : <div className={`absolute inset-0 ${bridge.heroHeadline ? 'bg-gray-900/55 lg:bg-gray-900/45' : 'bg-gray-900/40 lg:bg-gray-900/20'}`} aria-hidden="true" data-id="element-1720" data-mp="2c6zy5qgb2jon" />}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-1721" data-mp="1jiubuz9lsmmp">
            <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center" data-id="element-1722" data-mp="1ogm2zr2fc4pu">
              {bridge.heroHeadline ?
            // Headline leads on every viewport: stacked above the call to
            // action on mobile, beside it on desktop.
            <h1 className="mb-8 lg:mb-0 text-center lg:text-left text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-1723" data-mp="1ycyxgr5smmln">
                  {bridge.heroHeadline}
                </h1> : <div className="hidden lg:block" aria-hidden="true" data-id="element-1724" data-mp="y7mytaq5nk8v" />}
              <div className="w-full bg-white rounded-2xl shadow-2xl p-8 md:p-10 text-center max-w-xl mx-auto lg:mx-0" data-id="element-1725" data-mp="t81x4sjloxsg">
                {bridge.heroHeadline ? <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-1726" data-mp="ofh8s6jgrcwb">
                    {bridge.cardHeadline}
                  </h2> : <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-1727" data-mp="ejxflrwaasb9">
                    {bridge.cardHeadline}
                  </h1>}
                <p className="mt-4 text-lg font-semibold text-[#e8594f]" data-id="element-1728" data-mp="5ho4p0x3swxc">
                  {bridge.cardSubhead}
                </p>
                <button onClick={handleCta} className="mt-8 w-full sm:w-auto px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1729" data-mp="kxrclcooffm">
                  {bridge.ctaLabel}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* National education outcomes — compact band directly under the hero */}
        <section className="bg-gray-50 border-b border-gray-200" aria-label="Education outcomes" data-id="element-1730" data-mp="z1bks0s9kfqm">
          <dl className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid gap-6 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-gray-200" data-id="element-1731" data-mp="l1aj4j46d4a3">
            {highlights.map((item, i) => {
            const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
            return <div key={item.label} className="flex gap-4 sm:px-6 sm:first:pl-0 sm:last:pr-0" data-id="element-1732" data-mp="g27ciu8jqq00" data-mpi={i}>
                  <Icon className="mt-1 h-5 w-5 flex-shrink-0 text-[#e8594f]" aria-hidden="true" data-id="element-1733" data-mp="25hx5tgku3ono" />
                  <div data-id="element-1734" data-mp="3uxzp2qh4o74n">
                    <dt className="text-xl font-extrabold text-gray-900 tracking-tight leading-none" data-id="element-1735" data-mp="3uzaqwkr0j4t4">
                      {item.stat}{' '}
                      <span className="text-sm font-semibold text-gray-500" data-id="element-1736" data-mp="3pxlfh3zvop4i">
                        {item.label}
                      </span>
                      {item.note && <sup className="ml-0.5 text-[10px] font-semibold text-gray-400" data-id="element-1737" data-mp="3qwtlmu9beom">
                          [{item.note}]
                        </sup>}
                    </dt>
                    <dd className="mt-2 text-sm text-gray-600 leading-relaxed" data-id="element-1738" data-mp="dk3yl6l34so6">
                      {item.body}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </section>

        {/* Short paragraph section */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-1739" data-mp="2t9ffiudwikwk">
          <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-12 lg:items-start" data-id="element-1740" data-mp="2obduw50tudiz">
            <div data-id="element-1741" data-mp="1e7bmmzyg6s61">
              <span className="mx-auto lg:mx-0 mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#e8594f]/10" aria-hidden="true" data-id="element-1742" data-mp="1e7v5qjzu3mln">
                <GraduationCapIcon className="h-6 w-6 text-[#e8594f]" data-id="element-1743" data-mp="148vwp0b5poy4" />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center lg:text-left" data-id="element-1744" data-mp="107kcjab1xvxb">
                {bridge.sectionHeading}
              </h2>
              <div className="mt-8 space-y-5" data-id="element-1745" data-mp="1rxynaxnrqs4r">
                {bridge.paragraphs.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-lg text-gray-600 leading-relaxed" data-id="element-1746" data-mp="1n6qeo3u9jasn">
                    {paragraph}
                  </p>)}
              </div>
            </div>

            <img src="https://cdn.magicpatterns.com/patterns/generated-images/c770ed7b-e4d2-4980-b486-3ba0bc2b040d.jpg" alt="Graduate in cap and gown holding her young daughter after her commencement ceremony" className="mt-10 lg:mt-0 w-full max-w-sm mx-auto lg:max-w-none rounded-2xl object-cover aspect-[4/5]" loading="lazy" data-id="element-1747" data-mp="2fpdub3qhpxmc" />
          </div>

          {/* Related pages — the same program at other levels, and sibling
              programs in the same field. Gives a visitor who is close but not
              committed somewhere to go other than back. */}
          {bridge.relatedGroups?.length ? <div className="mt-14 grid gap-10 sm:grid-cols-2" data-id="element-1748" data-mp="laauo0thnkl4">
              {bridge.relatedGroups.map(group => <section key={group.heading} data-id="element-1749" data-mp="q4ov4hn0chwn">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500" data-id="element-1750" data-mp="u4fhm95i4bvd">
                    {group.heading}
                  </h3>
                  <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1751" data-mp="z15wwu9hf3fq">
                    {group.items.map(item => <li key={item.to} data-id="element-1752" data-mp="1400mqg5v9s9c">
                        <Link to={item.to} className="inline-flex items-center rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-[#e8594f] hover:text-[#e8594f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e8594f] focus-visible:ring-offset-2" data-id="element-1753" data-mp="304q7ji3k1lfn">
                          {item.label}
                        </Link>
                      </li>)}
                  </ul>
                </section>)}
            </div> : null}

          <div className="mt-14 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between rounded-2xl bg-gray-50 border border-gray-200 p-6 sm:p-8" data-id="element-1754" data-mp="3ssuk8nkjmyru">
            <p className="text-lg font-semibold text-gray-900 max-w-md" data-id="element-1755" data-mp="1yfmliktg630x">
              {bridge.closingLine || bridge.cardSubhead}
            </p>
            <button onClick={handleCta} className="w-full sm:w-auto flex-shrink-0 px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1756" data-mp="1tiw1jmeiuna8">
              {bridge.ctaLabel}
            </button>
          </div>

          {bridge.disclosure && <p className="mt-10 text-xs text-gray-400 leading-relaxed" data-id="element-1757" data-mp="3mdx1ziep1tpo">
              {bridge.disclosure}
            </p>}
        </section>

        {/* Citations for the national statistics above */}
        <section className="border-t border-gray-200 bg-white" aria-label="Citations" data-id="element-1758" data-mp="3cfzh94w1mf1t">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-1759" data-mp="3he0yrldtm6b6">
            <h2 className="text-sm font-bold text-gray-600 mb-3" data-id="element-1760" data-mp="1aomaeaailx34">Citations</h2>
            <ol className="space-y-1.5" data-id="element-1761" data-mp="1amjmpwwu4g1k">
              {EDUCATION_CITATIONS.links.map((href, i) => <li key={href} className="text-xs text-gray-500" data-id="element-1762" data-mp="1fl39u0n5zxvi" data-mpi={i}>
                  <span className="mr-1 text-gray-400" data-id="element-1763" data-mp="3bq2cg2vqurwf">[{i + 1}]</span>
                  <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="underline break-all hover:text-gray-700" data-id="element-1764" data-mp="37oqv0kn0xczb">
                    {href}
                  </a>
                </li>)}
            </ol>
            <div className="mt-5 space-y-1" data-id="element-1765" data-mp="2st15icimqhpu">
              {EDUCATION_CITATIONS.notes.map(note => <p key={note} className="text-xs text-gray-500 leading-relaxed" data-id="element-1766" data-mp="2nywvtlhzbvl2">
                  {note}
                </p>)}
            </div>
          </div>
        </section>
      </main>
      <Footer onNavigate={handleNavigate} data-id="element-1767" data-mp="2ey8ikt0etz98" />
    </div>;
}